// Module 19 (BL-380) G5 - QR codes for check-in passes, made in the browser (nothing is sent anywhere to draw them).
// Encoding: qrcode-generator (MIT, no dependencies). Reading, when the browser has no built-in QR reader: jsQR (Apache-2.0, no dependencies),
// loaded only when the door scanner needs it. A pass QR holds ONLY the opaque pass code (no name, email or phone).
import qrcode from 'qrcode-generator';

// The pass code the database hands out (migration 246): 'ev1.' + 64 hex characters.
export const PASS_CODE_PATTERN = /^ev1\.[0-9a-f]{64}$/;
export const isPassCode = (text) => PASS_CODE_PATTERN.test(String(text ?? '').trim().toLowerCase());

// The QR as a grid of dark / light modules (true = dark). Error correction M: still reads with a scuffed or creased print.
export function qrMatrix(text) {
    const code = qrcode(0, 'M');
    code.addData(String(text ?? ''), 'Byte');
    code.make();
    const size = code.getModuleCount();
    const rows = [];
    for (let r = 0; r < size; r += 1) {
        const row = [];
        for (let c = 0; c < size; c += 1) row.push(code.isDark(r, c));
        rows.push(row);
    }
    return rows;
}

// One SVG path for the dark modules, in module units, with a quiet zone of `margin` modules around it.
export function qrPath(matrix, margin = 4) {
    const parts = [];
    matrix.forEach((row, r) => row.forEach((dark, c) => { if (dark) parts.push(`M${c + margin} ${r + margin}h1v1h-1z`); }));
    return { d: parts.join(''), size: matrix.length + margin * 2 };
}

// The QR as a standalone SVG file (the download when a browser cannot draw a PNG).
export function qrSvgText(text, { margin = 4, title = 'Check-in pass' } = {}) {
    const { d, size } = qrPath(qrMatrix(text), margin);
    const safeTitle = String(title).replace(/[<>&"]/g, '');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size * 8}" height="${size * 8}" shape-rendering="crispEdges"><title>${safeTitle}</title><rect width="${size}" height="${size}" fill="#FFFFFF"/><path d="${d}" fill="#000000"/></svg>`;
}

// The QR as RGBA pixels (what a camera frame looks like to the reader). Used by the fallback reader's test and by the PNG download.
export function qrPixels(text, { scale = 8, margin = 4 } = {}) {
    const matrix = qrMatrix(text);
    const modules = matrix.length + margin * 2;
    const width = modules * scale;
    const data = new Uint8ClampedArray(width * width * 4).fill(255);
    matrix.forEach((row, r) => row.forEach((dark, c) => {
        if (!dark) return;
        for (let y = (r + margin) * scale; y < (r + margin + 1) * scale; y += 1) {
            for (let x = (c + margin) * scale; x < (c + margin + 1) * scale; x += 1) {
                const i = (y * width + x) * 4;
                data[i] = 0; data[i + 1] = 0; data[i + 2] = 0;
            }
        }
    }));
    return { data, width, height: width };
}

// The reader for the door scanner: the browser's own BarcodeDetector when it can read QR codes, otherwise jsQR on a camera frame.
// `win` and `loadFallback` are parameters so the choice can be tested without a camera.
export async function createQrReader(win = (typeof window !== 'undefined' ? window : undefined), loadFallback = () => import('jsqr')) {
    const Detector = win && win.BarcodeDetector;
    if (Detector) {
        try {
            const formats = typeof Detector.getSupportedFormats === 'function' ? await Detector.getSupportedFormats() : ['qr_code'];
            if (formats.includes('qr_code')) {
                const detector = new Detector({ formats: ['qr_code'] });
                return {
                    engine: 'native',
                    async read(video) {
                        const found = await detector.detect(video);
                        return found && found[0] ? String(found[0].rawValue || '') : '';
                    },
                };
            }
        } catch {
            // fall through to the fallback reader
        }
    }
    const module = await loadFallback();
    const jsQR = module.default || module;
    let canvas = null;
    return {
        engine: 'fallback',
        // `source` is a <video> (a frame is copied to a canvas) or { data, width, height } pixels.
        async read(source) {
            let pixels = source;
            if (source && typeof source.videoWidth === 'number') {
                const width = source.videoWidth; const height = source.videoHeight;
                if (!width || !height) return '';
                canvas = canvas || win.document.createElement('canvas');
                canvas.width = width; canvas.height = height;
                const context = canvas.getContext('2d', { willReadFrequently: true });
                if (!context) return '';
                context.drawImage(source, 0, 0, width, height);
                pixels = context.getImageData(0, 0, width, height);
            }
            if (!pixels || !pixels.data) return '';
            const result = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' });
            return result ? String(result.data || '') : '';
        },
    };
}
